import { DATE_PIPE_DEFAULT_OPTIONS, DatePipeConfig } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  isDevMode,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import {
  customerContextEffects,
  customerContextInterceptor,
} from '@kinet-open-network/client-zone/context/data-access';
import { provideZonePayConfig } from '@kinet-open-network/client-zone/invoices/data-access';
import {
  AuthInterceptor,
  authEffects,
  authFeatureKey,
  authReducer,
  provideAuthConfig,
} from '@kinet-open-network/client/auth/data-access';
import {
  API_URL,
  apiUrlInterceptor,
} from '@kinet-open-network/client/core/api-client';
import {
  errorHandlerEffects,
  errorHandlerFeatureKey,
  errorHandlerReducer,
  errorHandlingInterceptor,
} from '@kinet-open-network/client/core/error-handler';
import { provideDynamicForms } from '@kinet-open-network/client/core/forms/ui';
import {
  routerFeatureKey,
  routerReducer,
} from '@kinet-open-network/client/core/router';
import { TABLE_STATE_KEY_PREFIX } from '@kinet-open-network/client/shared/ui';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { ZoneTheme } from './app.zone-theme';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withEnabledBlockingInitialNavigation(),
      withComponentInputBinding(),
    ),
    provideStore({
      [authFeatureKey]: authReducer,
      [errorHandlerFeatureKey]: errorHandlerReducer,
      [routerFeatureKey]: routerReducer,
    }),
    provideEffects(authEffects, errorHandlerEffects, customerContextEffects),
    provideAuthConfig({
      show2faPrompt: !environment.disable_2fa_prompt,
    }),
    // provideRouterStore(),
    isDevMode() ? provideStoreDevtools() : [],
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        apiUrlInterceptor,
        errorHandlingInterceptor,
        customerContextInterceptor,
      ]),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: API_URL,
      useValue: environment.api_url || 'http://localhost:3015/api',
    },
    {
      provide: LOCALE_ID,
      useValue: 'sk-SK',
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        dateFormat: 'd. MMM yyyy, HH:mm',
      } satisfies DatePipeConfig,
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR',
    },
    {
      provide: TABLE_STATE_KEY_PREFIX,
      useValue: 'kon_',
    },
    provideDynamicForms(),
    provideAnimations(),
    MessageService,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideZonePayConfig({
      enableCustomPaymentAmount: !!environment.enable_custom_payment_amount,
    }),
    providePrimeNG({
      theme: {
        preset: ZoneTheme,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
          darkModeSelector: 'none',
        },
      },
    }),
  ],
};
